import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { cn } from "../../utils/cn";
import { IndicationUnit } from "../Metrics/IndicationUnit";

/**
 * IndicationUnit `status` states the dot can render.
 *
 * `pending` — amber / in-progress tone (via `--color-warning-base`), read as
 * "activating / not yet live." It sits between Draft (also amber-toned, but
 * labelled "Draft") and the green Active. NOTE: `pending` is currently a
 * **sim / forthcoming** program status — the program-activation sim
 * ({@link ../../widgets/yaas/programs/ProgramActivationSim}) uses it to show
 * the moment between "Add Program" and the program going Active; real backend
 * support for a distinct "Pending" program lifecycle status is TBD. The state
 * itself is already a first-class `IndicationUnit` status (draft badges use it
 * too), so this is additive and breaks no existing Draft/Active/Ended callers.
 */
type StatusBadgeState = "success" | "pending" | "disabled" | "error";

export interface StatusBadgeProps {
    /**
     * Dot state — drives the IndicationUnit `status` palette
     * (success = green, pending = amber, disabled = grey, error = red).
     * The caller maps its domain status (e.g. a program status) to this
     * state, so this badge stays domain-free.
     */
    state: StatusBadgeState;
    /** The status text shown beside the dot (e.g. "Active" / "Draft"). */
    label: React.ReactNode;
    /** Dot size in px. Defaults to 6 (the create-flow title-block size). */
    dotSize?: number;
    /** Text-color utility for the label (e.g. `text-success-base`). */
    labelClassName?: string;
    /** Extend / override the wrapper chrome. */
    className?: string;
}

/**
 * StatusBadge — a bare status dot + label: an `IndicationUnit` status dot
 * next to a `Text` label, inline, with no pill chrome. The flat, editorial
 * treatment used in the create-flow title block (distinct from the console
 * `LifecycleStatusBadge` pill).
 *
 * Generic Component: it knows no Solomon/YaaS entity. The `state`,
 * `label`, and `labelClassName` all come in as props — the caller passes
 * the domain-status → state/label/tone mapping in — so this badge never
 * imports a Domain Component. Composes the existing `IndicationUnit` and
 * `Text` primitives.
 *
 * @version 1.0.0
 * @published 2026-07-04
 * @history
 *   1.0.0 — Initial publish. Extracted from the client Programs page's
 *           page-local StatusBadge as a domain-free GC (status → label/
 *           tone mapping lifted into the caller).
 *
 * @see StatusBadge.intent.md   policy + recipes
 * @see StatusBadge.layout.html intended layout
 */
export function StatusBadge({
    state,
    label,
    dotSize = 6,
    labelClassName,
    className
}: StatusBadgeProps): React.ReactElement {
    return (
        <span className={cn("inline-flex items-center gap-2", className)}>
            <IndicationUnit variant="status" state={state} size={dotSize} />
            <Text variant="small" render={<span />} className={labelClassName}>
                {label}
            </Text>
        </span>
    );
}
